import React, { Component } from 'react'
import { connect } from 'react-redux'
import { toWidth, apiFetch, getTheme, viewportWidth, viewportHeight } from 'helpers'
import styled from 'styled-components'
import { get, isEqual } from 'lodash'
import { ObjectID } from 'bson'
import { List } from 'react-virtualized'

import NoData from 'components/NoData'
import AspectRatioImage, { AspectRatioImageContainer } from 'components/AspectRatioImage'
import Modal, { SlideupView, Overlay, ConfirmDialog } from 'components/Modal'
import HorizontalCentered from 'components/HorizontalCentered'
import VerticalCentered from 'components/VerticalCentered'
import FullscreenPhoto from 'components/FullscreenPhoto'
import AlbumItem, { AlbumItemWrapper } from 'components/AlbumItem'
import PhotoItem, { PhotoItemWrapper } from 'components/PhotoItem'
import Loader from 'components/Loader'
import svgNoPhotos from 'assets/svg/icon-no-photos.svg'
import svgPlus from 'assets/svg/icon-plus.svg'
import svgPlusTiffany from 'assets/svg/icon-plus-tiffany.svg'
import svgCloseWhite from 'assets/svg/icon-close-white.svg'
import { SectionHeader, SectionHeaderText, SectionHeaderRight, ItemWrapper } from './Section'
import { uploadToS3 } from '../../helpers'

@connect(state => ({
  loading: get(state, 'vendor.findFeaturedPhotosAttempting'),
  featuredPhotos: get(state, 'vendor.featuredPhotos') || [],
  me: get(state, 'vendor.me'),
  albums: get(state, 'vendor.albums') || [],
  albumsLoading: get(state, 'vendor.findAlbumsAttempting') || false
}))
export default class extends Component {
  constructor (props) {
    super(props)
    this.state = {
      edited: false,
      featuredPhotos: props.featuredPhotos || []
    }
  }

  componentDidMount () {
    const { dispatch } = this.props
    apiFetch({
      dispatch,
      key: 'FIND_FEATURED_PHOTOS',
      v4: true,
      url: '/v4/vendors/me/photos',
      query: { limit: 12, featured: 1 }
    })
  }

  componentWillReceiveProps (nextProps) {
    if (!isEqual(this.props.featuredPhotos, nextProps.featuredPhotos)) {
      this.setState({ featuredPhotos: nextProps.featuredPhotos })
    }
  }

  componentWillUpdate (nextProps, nextState) {
    const thisAlbumId = get(this.state, 'currentAlbum._id')
    const nextAlbumId = get(nextState, 'currentAlbum._id')
    if (!thisAlbumId && nextAlbumId) {
      this.setState({ albumsPhotoLoading: true }, () =>
        apiFetch({
          v4: true,
          url: `/v4/vendors/me/albums/${nextAlbumId}/photos`,
          query: {
            limit: 'noLimit'
          }
        })
          .then((albumPhotos) => this.setState({ albumsPhotoLoading: false, albumPhotos }))
          .catch(() => this.setState({ albumsPhotoLoading: false }))
      )
    } else if (thisAlbumId && !nextAlbumId) {
      this.setState({ albumPhotos: [] })
    }
  }

  _onUnpinPhoto = () => {
    const photoId = this.state.unpinningPhotoId || get(this.state, 'currentPhoto._id')
    this.setState({ unpinning: true }, () =>
      apiFetch({
        dispatch: this.props.dispatch,
        key: 'REMOVE_FEATURED_PHOTO',
        v4: true,
        url: `/v4/vendors/me/photos/${photoId}/unpin`,
        method: 'PUT'
      })
        .then(() => this.setState({
          unpinning: false,
          confirmUnpinVisible: false,
          featuredPhotos: this.state.featuredPhotos.filter(_photo => _photo._id !== photoId)
        }))
        .catch(() => this.setState({ unpinning: false }))
    )
  }

  _onFileChosen = (e) => {
    const { dispatch, featuredPhotos = [] } = this.props
    const files = Object.keys(e.target.files).map(key => e.target.files[key]).slice(0, 12 - Math.min(featuredPhotos.length, 12))
    files.forEach(file => dispatch({ type: 'ADD_FEATURED_PHOTO_ATTEMPT', file, imagePath: null }))
    files.reduce((promise, file, index) =>
      promise.then(() => {
        const photoId = new ObjectID().toString()
        this.setState({ featuredPhotos: (this.state.featuredPhotos || []).concat({ _id: photoId }) },
          () =>
            apiFetch({
              method: 'POST',
              v4: true,
              url: '/v4/s3/signed-url',
              body: {
                collectionName: 'vendors',
                folderName: `${get(this.props, 'me._id')}/featured`,
                fileName: `${photoId}.${file.type.split('/').pop()}`,
                fileType: file.type
              }
            })
              .then(({ url }) => uploadToS3({ url, file }))
              .then(imagePath => new Promise(resolve => {
                const img = new window.Image()
                img.onload = () => resolve({ imagePath, ratioW2H: img.width / img.height })
                img.src = (window.URL || window.webkitURL).createObjectURL(file)
              }))
              .then(({ imagePath, ratioW2H }) =>
                apiFetch({
                  method: 'POST',
                  v4: true,
                  url: '/v4/vendors/me/photos/featured',
                  body: {
                    photoId,
                    imagePath,
                    ratioW2H
                  }
                })
              )
              .then((photo) => {
                this.setState({
                  featuredPhotos: this.state.featuredPhotos.map(_photo =>
                    _photo._id === photo._id ? photo : _photo
                  )
                })
                return Promise.resolve()
              })
        )
      })
      , Promise.resolve())
  }

  _onPhotoChosen = (photo, pinnedPosition) => {
    this.setState({ pinning: true }, () =>
      apiFetch({
        v4: true,
        url: `/v4/vendors/me/photos/${photo._id}/pin`,
        method: 'PUT',
        body: {
          pinnedPosition
        }
      })
        .then(() => this.setState({
          featuredPhotos: this.state.featuredPhotos.concat([photo]),
          pinning: false,
          albumChooserVisible: false,
          currentAlbum: null
        }))
        .catch(error => this.setState({ error, pinning: false }))
    )
  }

  _col3Width = () => (viewportWidth() - 20) / 3

  _albumsRenderer = ({ key, index, style = {}, className }) => {
    const colCount = this._columnCount()
    const { albums, albumsLoading } = this.props
    const currentRowAlbums = (albums || []).slice(index * colCount, (index + 1) * colCount)
    return (
      <Row key={key} className={className} style={style}>
        {currentRowAlbums.map(album =>
          <AlbumItemWrapper key={album._id} style={{ marginTop: 5 }}>
            <AlbumItem loading={albumsLoading}
              album={album} width={this._col3Width()}
              onClick={() => this.setState({ currentAlbum: album })} />
          </AlbumItemWrapper>
        )}
      </Row>
    )
  }

  _columnCount = () => {
    const cw = Math.min(viewportWidth(), 1080)
    if (cw >= 960) {
      return 6
    } else if (cw >= 768) {
      return 5
    } else if (cw >= 600) {
      return 4
    }
    return 3
  }

  _columnWidth = () => {
    const colCount = this._columnCount()
    return (Math.min(viewportWidth(), 1080) / colCount) - ((colCount + 1) * 5) / colCount
  }

  _photosRenderer = ({ key, index, style = {}, className }) => {
    const colCount = this._columnCount()
    const { albumPhotos, albumPhotosLoading } = this.state
    const currentRowPhotos = (albumPhotos || []).slice(index * colCount, (index + 1) * colCount)
    return (
      <Row key={key} className={className} style={style}>
        {currentRowPhotos.map(photo =>
          <PhotoItemWrapper key={photo._id} style={{ marginTop: 5 }}>
            <PhotoItem loading={albumPhotosLoading}
              onClick={() => this._onPhotoChosen(photo, 13 - index)}
              photo={photo} width={this._columnWidth()} />
          </PhotoItemWrapper>
        )}
      </Row>
    )
  }

  render () {
    const { loading, albums } = this.props
    const { featuredPhotos = [] } = this.state
    return (
      <SectionPhotos>
        <SectionHeader>
          <SectionHeaderText>ภาพไฮไลท์</SectionHeaderText>
          <BtnDesktopEdit onClick={() => this.setState({ edited: true })}>เลือกภาพ</BtnDesktopEdit>
          <SectionHeaderRight>
            <BtnEdit onClick={() => this.setState({ edited: true })}>เลือกภาพ</BtnEdit>
          </SectionHeaderRight>
        </SectionHeader>
        <SlideupView viewType='full'
          title='เลือกภาพไฮไลท์'
          visible={this.state.edited}
          onClose={() => this.setState({ edited: false })}>
          {featuredPhotos.length < 12 && (
            <BtnAddPhoto onClick={() => this.refs.fileChooser.click()}>
              <img alt='' src={svgPlusTiffany} />
              <span>อัพโหลดภาพใหม่</span>
              <input type='file' ref='fileChooser'
                multiple
                style={{ display: 'none' }}
                onChange={this._onFileChosen} />
            </BtnAddPhoto>
          )}
          <SlideupViewBody>
            {featuredPhotos.slice(0, 12).map(photo =>
              get(photo, 'imagePath') ? (
                <ModalPhotoWrapper key={photo._id}>
                  <AspectRatioImage src={toWidth(get(photo, 'imagePath'), 300)} />
                  <BtnRemove onClick={() => this.setState({ confirmUnpinVisible: true, unpinningPhotoId: photo._id })}>
                    <IconRemove src={svgCloseWhite} />
                  </BtnRemove>
                </ModalPhotoWrapper>
              ) : (
                <ModalPhotoWrapper>
                  <Loader />
                </ModalPhotoWrapper>
              )
            )}
            {Array(this.state.edited ? (12 - Math.min(featuredPhotos.length, 12)) : 0).fill().map((_, index) =>
              <ModalPhotoWrapper key={index}>
                <BtnPinPhoto onClick={() => this.setState({ albumChooserVisible: true })}>
                  <IconPlus src={svgPlus} />
                </BtnPinPhoto>
              </ModalPhotoWrapper>
            )}
          </SlideupViewBody>
        </SlideupView>
        <Overlay visible={this.state.albumChooserVisible}
          onClose={() => this.setState({ albumChooserVisible: false })}>
          <HorizontalCentered>
            <VerticalCentered>
              {get(this.state.currentAlbum, '_id') ? (
                <Modal auto noPadding title={get(this.state, 'currentAlbum.name')}
                  onBack={() => this.setState({ currentAlbum: null })}
                  onClose={() => this.setState({ currentAlbum: null })}>
                  <List
                    rowCount={Math.ceil((this.state.albumPhotos || []).length / 3)}
                    style={{ outline: 'none' }}
                    scrollToAlignment='start'
                    rowRenderer={this._photosRenderer}
                    rowHeight={() => this._columnWidth() + 5}
                    width={viewportWidth() > 1080 ? 1080 : (viewportWidth() - 20)}
                    height={0.9 * viewportHeight()} />
                </Modal>
              ) : (
                <Modal auto
                  noPadding title='เลือกรูปภาพจากอัลบั้ม'
                  onClose={() => this.setState({ albumChooserVisible: false })}>
                  <List
                    rowCount={Math.ceil((albums || []).length / 6)}
                    style={{ outline: 'none', padding: '5px 0px' }}
                    scrollToAlignment='start'
                    rowRenderer={this._albumsRenderer}
                    rowHeight={() => this._columnWidth() + 45}
                    width={viewportWidth() > 1080 ? 1080 : (viewportWidth() - 20)}
                    height={0.9 * viewportHeight()} />
                </Modal>
              )}

            </VerticalCentered>
          </HorizontalCentered>
        </Overlay>
        <Overlay visible={this.state.confirmUnpinVisible}
          onClose={() => this.setState({ confirmUnpinVisible: false })}>
          <ConfirmDialog title='คุณต้องการลบภาพนี้ออกจากภาพไฮไลท์ ?'
            loading={this.state.unpinning}
            onOkay={this._onUnpinPhoto}
            onCancel={() => this.setState({ confirmUnpinVisible: false })} />
        </Overlay>
        <FullscreenPhoto
          photos={featuredPhotos}
          currentPhoto={this.state.currentPhoto}
          onUnpin={this._onUnpinPhoto}
          unpinning={this.state.unpinning}
          onClose={() => this.setState({ currentPhoto: null })} />
        {loading ? (
          <SectionBody>
            {Array(12).fill().map((_, index) =>
              <ItemWrapper key={index}>
                <PhotoWrapper>
                  <Loader />
                </PhotoWrapper>
              </ItemWrapper>
            )}
          </SectionBody>
        ) : get(featuredPhotos, 0) ? (
          <SectionBody>
            <div>
              {featuredPhotos.map(photo =>
                <ItemWrapper showAll key={photo._id} count={featuredPhotos.length} unit='รูปภาพ'>
                  <PhotoWrapper onClick={() => this.setState({ currentPhoto: photo })}>
                    <AspectRatioImage src={toWidth(get(photo, 'imagePath'), 300)} />
                  </PhotoWrapper>
                </ItemWrapper>
              )}
            </div>
          </SectionBody>
        ) : (
          <SectionBody>
            <NoData iconSrc={svgNoPhotos} title='ยังไม่มีภาพไฮไลท์' />
          </SectionBody>
        )}
      </SectionPhotos>
    )
  }
}

const SectionPhotos = styled.div`
  margin: 0 auto;
  max-width: 1080px;
  padding-top: 30px;
`
const BtnEdit = styled.div`
  color: ${getTheme`colorTiffany`};
  font-size: 16px;
`
const BtnDesktopEdit = styled.div`
  text-align: center;
  color: ${getTheme`colorTiffany`};
  font-size: 18px;
  margin-bottom: 15px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  @media(max-width: 767px) { display: none; }
`
const BtnAddPhoto = styled.div`
  border-radius: 5px;
  border: 1px solid ${getTheme`colorTiffanyDark`};
  height: 40px;
  line-height: 40px;
  text-align: center;
  font-family: ${getTheme`font1`};
  font-size: 18px;
  color: ${getTheme`colorTiffanyDark`};
  max-width: 420px;
  margin: 0px auto 15px;
  cursor: pointer;
  &:hover {
    border: 1px solid ${getTheme`colorTiffany`};;
    color: ${getTheme`colorTiffany`};
  }
  > img {
    vertical-align: top;
    margin-top: 12px;
    margin-right: 10px;
    width: 16px;
    height: 16px;
    vertical-align: top;
  }
`
const SectionBody = styled.div`
  position: relative;
  z-index: 2;
  @media(max-width: 413px) {
    padding: 0px 10px;
  }
`
const PhotoWrapper = styled(AspectRatioImageContainer)`
  display: inline-block;
  vertical-align: top;
  width: 100%;
  padding-bottom: 100%;
  margin-right: 5px;
  margin-bottom: 5px;
`
const ModalPhotoWrapper = styled(AspectRatioImageContainer)`
  display: inline-block;
  vertical-align: top;
  width: calc(33.33% - 4px);
  padding-bottom: calc(33.33% - 4px);
  margin-right: 5px;
  margin-bottom: 5px;
  &:nth-child(3n) { margin-right: 0px; }
`
const BtnPinPhoto = styled.div`
  border: 1px solid ${getTheme`colorBorder`};
  text-align: center;
  cursor: pointer;
  background-color: white;
  transition: background-color 0.3s ease;
  &:hover { background-color: ${getTheme`colorBorder`}; }
`
const IconPlus = styled.img`
  width: 40px;
  padding: calc(50% - 22px) 0px;
`
const BtnRemove = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`
const IconRemove = styled.img`
  width: 12px;
  height: 12px;
  vertical-align: top;
`
const Row = styled.div`
  width: calc(100% - 10px) !important;
  padding: 0px 5px;
`
const SlideupViewBody = styled.div`
  white-space: initial;
`



// WEBPACK FOOTER //
// ./src/components/profile/SectionPhotos.js